import classNames from 'classnames'
import React, { ReactNode } from 'react'

import { SetAddOnDTO } from '../../../server/database/models/AddOnModel.js'
import { Accordion, AccordionItem } from '../../components/accordion/accordion.js'
import { Button } from '../../components/button/button.js'
import { Card } from '../../components/card/card.js'
import { Checkbox } from '../../components/forms/checkbox/checkbox.js'
import { OptionCardGroup, OptionCardGroupProps } from '../../components/forms/option-card-group/option-card-group.js'
import { OptionCard, OptionCardProps } from '../../components/forms/option-card/option-card.js'

import './plan-cards.scss'

export interface PlanCardsAddOnProps extends SetAddOnDTO {
    onChange: (checked: boolean) => void
}

export interface PlanCardsProps {
    title: string
    description: ReactNode | undefined
    plans: PlanCardProps[]
    groupProps: OptionCardGroupProps
    disclaimer?: ReactNode
    addOns?: PlanCardsAddOnProps[]
    welcomeMessage?: {
        title?: string
        paragraphs: string[]
    }
}

export interface PlanCardProps extends OptionCardProps {
    title: string
    description: ReactNode | undefined
    price: ReactNode
    priceDescription: ReactNode | undefined
    tradeInDiscount?: ReactNode | undefined
    buttonId?: string
    buttonText?: string
    accordion?: PlanCardAccordionProps
    minimumCost?: string
    type?: 'minimal'
}

export interface PlanCardAccordionProps {
    title: string
    content: ReactNode
}

export interface SetAddOnDTOProps extends SetAddOnDTO {
    isSelected?: boolean
    onChange?: (isSelected: boolean) => void
}

const renderOptionCard = (cardProps: PlanCardProps, isChecked: boolean): JSX.Element => {
    const titleClass = classNames('text', {
        h4: cardProps.type === 'minimal',
    })

    const descriptionClass = classNames('card__description', {
        'text-small': cardProps.type !== 'minimal',
        'text-tiny': cardProps.type === 'minimal',
    })
    return (
        <OptionCard {...cardProps} key={encodeURIComponent(cardProps.title)}>
            <div className="option-card__top text">
                <h2 className={titleClass}>{cardProps.title}</h2>
                <div className={descriptionClass}>{cardProps.description}</div>
            </div>
            <div className="option-card__mid text">
                <p className="text-large text-price">{cardProps.price}</p>
                {cardProps.priceDescription}
                {cardProps.tradeInDiscount}
            </div>
            {cardProps.buttonText && (
                <div className="option-card__bottom text">
                    <Button
                        id={cardProps.buttonId}
                        isBlock={true}
                        text={cardProps.buttonText}
                        variant={isChecked ? 'primary' : 'secondary'}
                    />
                    {cardProps.minimumCost && (
                        <p className="text-tiny h-text-center text-break-spaces">{cardProps.minimumCost}</p>
                    )}
                </div>
            )}
        </OptionCard>
    )
}

const getAddOnLabel = (addOn: SetAddOnDTOProps): ReactNode => {
    return (
        <div className="text">
            {addOn.subtitle && <h4 className="bold">{addOn.subtitle}</h4>}
            <p className={classNames('text-small', addOn.subtitle && 'text__margin-half')}>{addOn.fullText}</p>
        </div>
    )
}

const renderOptionAccordion = (
    cardProps: PlanCardProps,
    isVisible: boolean,
    isOpen: boolean,
    onChange: () => void,
): JSX.Element | null => {
    if (cardProps.accordion) {
        const id = `${encodeURIComponent(cardProps.title)}-accordion-item`
        const accordion = cardProps.accordion

        return (
            <Accordion
                className={classNames({ 'h-hidden': !isVisible })}
                key={`${encodeURIComponent(cardProps.title)}-accordion`}
                onChange={() => onChange()}
                value={isOpen ? id : []}
            >
                <AccordionItem id={id} label={<span className="h1">{accordion.title}</span>}>
                    {accordion.content}
                </AccordionItem>
            </Accordion>
        )
    } else {
        return null
    }
}

export const PlanCards = (props: PlanCardsProps): JSX.Element => {
    const [isAccordionOpen, setIsAccordionOpen] = React.useState<boolean>(true)

    const onAccordionToggle = () => {
        setIsAccordionOpen(!isAccordionOpen)
    }

    return (
        <div className="text">
            <p className="bold">{props.title}</p>
            <p>{props.description}</p>

            <OptionCardGroup {...props.groupProps}>
                {props.plans?.map((cardProps: PlanCardProps) =>
                    renderOptionCard(cardProps, props.groupProps.value === cardProps.value),
                )}
            </OptionCardGroup>
            {props.disclaimer && <p className="disclaimer">{props.disclaimer}</p>}

            {props.plans?.map((cardProps: PlanCardProps) =>
                renderOptionAccordion(
                    cardProps,
                    props.groupProps.value === cardProps.value,
                    isAccordionOpen,
                    onAccordionToggle,
                ),
            )}

            {props.addOns?.map((addOn: SetAddOnDTOProps) => (
                <Card
                    className="addon"
                    padding="medium"
                    hasContent={true}
                    key={encodeURIComponent(addOn.uuid)}
                    variant="quinary"
                >
                    {addOn.title && (
                        <div className="addon__header">
                            {addOn.image && <img src={addOn.image} alt={addOn.title} width="65px" />}
                            <h4 className="text-price">{addOn.title}</h4>
                        </div>
                    )}
                    <Checkbox
                        {...{
                            id: `add-on-${encodeURIComponent(addOn.uuid)}`,
                            label: getAddOnLabel(addOn),
                            value: addOn.uuid,
                            checked: addOn.isSelected,
                            onChange: addOn.onChange,
                        }}
                    />
                </Card>
            ))}
            {(props.welcomeMessage?.title || props.welcomeMessage?.paragraphs) && (
                <div className="text text-small">
                    {props.welcomeMessage.title && <h2>{props.welcomeMessage.title}</h2>}
                    {(Array.isArray(props.welcomeMessage.paragraphs) ? props.welcomeMessage.paragraphs : []).map(
                        (paragraph, index) => (
                            <p key={`welcome-${index}`} dangerouslySetInnerHTML={{ __html: paragraph }}></p>
                        ),
                    )}
                </div>
            )}
        </div>
    )
}
